import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { 
  Camera, CameraOff, AlertTriangle, CheckCircle,
  RotateCcw, Maximize2, Volume2, VolumeX,
  Settings2, RefreshCw
} from 'lucide-react';
import { Button } from './ui/button';
import { usePoseDetection3D, getRepThresholds, setCameraViewOverride, getTrackableMetrics } from '../hooks/usePoseDetection3D';
import { tempoIntent } from '../utils/coachLanguage';
import BodyMapSVG from './BodyMapSVG';

const Toggle = memo(({ checked, onChange, label, indent }) => (
  <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
    className={`flex items-center justify-between w-full py-1 ${indent ? 'pl-2' : ''}`}>
    <span className={`text-sm ${indent ? 'text-zinc-500' : 'text-zinc-400'}`}>{label}</span>
    <div className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 ${checked ? 'bg-[#CCFF00]' : 'bg-zinc-600'}`}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </div>
  </button>
));

const getCameraPlacement = (exerciseName) => {
  const e = (exerciseName || '').toLowerCase();
  if (e.includes('squat')) return { view: 'SIDE', desc: 'Place camera to your side at hip height. Full body visible, 6-8 ft away.' };
  if (e.includes('deadlift') || e.includes('rdl')) return { view: 'SIDE', desc: 'Side view at hip height. Essential for hip hinge tracking.' };
  if (e.includes('bench') || e.includes('press') || e.includes('push')) return { view: 'FRONT/SIDE', desc: 'Front or 45-degree angle at chest height.' };
  return { view: 'SIDE', desc: 'Side view at hip height. Full body visible.' };
};

const STATUS_BG = { good: 'bg-green-500/15 border-green-500/30', warning: 'bg-yellow-500/15 border-yellow-500/30', danger: 'bg-red-500/15 border-red-500/30' };
const STATUS_TEXT = { good: 'text-green-400', warning: 'text-yellow-400', danger: 'text-red-400' };

const Checkpoint = memo(({ cp }) => (
  <div className={`border rounded-sm px-3 py-2 ${STATUS_BG[cp.status]}`} data-testid={`checkpoint-${cp.label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-zinc-400 uppercase">{cp.label}</span>
      <span className={`text-sm font-bold font-mono ${STATUS_TEXT[cp.status]}`}>
        {cp.value || '--'}{cp.unit ? ` ${cp.unit}` : ''}
      </span>
    </div>
    <p className="text-xs text-zinc-500 mt-0.5">{cp.tip}</p>
  </div>
));

const IdealAnglesPanel = memo(({ idealAngles }) => {
  if (!idealAngles || Object.keys(idealAngles).length === 0) return null;
  return (
    <div className="hpme-card p-4 space-y-3" data-testid="ideal-angles-panel">
      <p className="font-secondary font-bold text-sm text-primary tracking-wider">IDEAL FORM</p>
      {Object.entries(idealAngles).map(([name, data]) => (
        <div key={name} className="border-b border-zinc-800 pb-2 last:border-0">
          <div className="flex justify-between items-center">
            <span className="text-xs text-zinc-400">{name}</span>
            <span className="text-xs font-mono text-primary">{data.ideal}{data.unit ? ` ${data.unit}` : ''}</span>
          </div>
          <p className="text-xs text-zinc-500">{data.desc}</p>
        </div>
      ))}
    </div>
  );
});

const PoseCameraAdvanced = ({ 
  exerciseName = 'Squat', 
  exerciseDefinition = null,
  setNumber = 1,
  onMetricsUpdate, 
  onRepComplete,
  onSetPathData,
  showLiveFeedback = true,
  isRecording = false,
  onRecordingComplete,
  liveFatigue = {},
  onRecordingReady,
  onCameraStateChange,
  immersive = false,
  mode = 'reps', // 'reps' = full counting+form; 'mirror' = skeleton-only, no counting/form
}) => {
  const {
    videoRef, canvasRef, isLoading, error,
    landmarks3D, exerciseAnalysis,
    cameraActive, startCamera, stopCamera, setExerciseName, setExerciseDefinition,
    isRecording: is3DRecording, recordedFrames, recordedVideoBlob,
    startRecording, stopRecording, clearRecording
  } = usePoseDetection3D(true);

  const [showPlacementGuide, setShowPlacementGuide] = useState(true);
  const [repCount, setRepCount] = useState(0);
  const [phase, setPhase] = useState('standing');
  const [showSettings, setShowSettings] = useState(false);
  const [showIdealPanel, setShowIdealPanel] = useState(true);
  const [audioFeedback, setAudioFeedback] = useState(false);
  
  const [facingMode, setFacingMode] = useState('user'); // 'user' = front, 'environment' = back
  const [viewMode, setViewMode] = useState('auto'); // 'auto' | 'front' | 'side'

  // Apply view override to the analyzer
  useEffect(() => {
    setCameraViewOverride(viewMode === 'auto' ? null : viewMode);
  }, [viewMode]);

  // Expose recording controls to parent
  useEffect(() => {
    if (onRecordingReady) {
      onRecordingReady({ startRecording, stopRecording, clearRecording });
    }
  }, [onRecordingReady, startRecording, stopRecording, clearRecording]);

  // Notify parent whenever camera turns on/off so it can toggle immersive layout.
  useEffect(() => {
    if (onCameraStateChange) onCameraStateChange(cameraActive);
  }, [cameraActive, onCameraStateChange]);

  const flipCamera = useCallback(() => {
    const newMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newMode);
    // Restart camera with new mode if already active
    if (cameraActive) {
      stopCamera();
      setTimeout(() => startCamera(newMode), 300);
    }
  }, [facingMode, cameraActive, stopCamera, startCamera]);

  const containerRef = useRef(null);
  const repStateRef = useRef('idle'); // idle → descending → bottom → ascending → (count) → idle
  const repCooldownRef = useRef(0);
  const repStartTimeRef = useRef(0);
  const deepStartTimeRef = useRef(0);
  const startAngleRef = useRef(null); // angle at top of movement
  const peakAngleChangeRef = useRef(0); // max angle change during this rep
  const peakDepthDuringRepRef = useRef(0); // max depthPct reached during this rep
  const speechSynthRef = useRef(null);
  const pendingStartRef = useRef(false);
  const audioCooldownRef = useRef({});
  const repDurationsRef = useRef([]);
  const baselineRef = useRef(null);
  const constraintViolationCountRef = useRef(0);
  // Per-rep joint angle accumulator — captures {min, max, sum, count} for every named angle
  // while the rep is in progress (not idle). Reset on descending-start, flushed at rep-complete.
  const repAngleAccumRef = useRef({});
  const personalMaxDepthRef = useRef(0); // personal calibration: max depth seen in first reps
  const [paceStatus, setPaceStatus] = useState(null); // { key: 'on-pace'|'slowing'|'struggling', label, tone, intent }

  const placement = getCameraPlacement(exerciseName);

  useEffect(() => { setExerciseName(exerciseName); }, [exerciseName, setExerciseName]);
  useEffect(() => { setExerciseDefinition(exerciseDefinition); }, [exerciseDefinition, setExerciseDefinition]);

  // Reset rep state when the exercise or set changes. Done as a render-time
  // sync (React's recommended pattern) instead of an effect — avoids
  // set-state-in-effect while keeping the proven live rep-counter behaviour
  // exactly the same.
  const resetKey = `${exerciseName}__${setNumber}`;
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  if (resetKey !== prevResetKey) {
    setPrevResetKey(resetKey);
    setRepCount(0);
    setPhase('standing');
    setPaceStatus(null);
    repStateRef.current = 'idle';
    repStartTimeRef.current = 0;
    deepStartTimeRef.current = 0;
    startAngleRef.current = null;
    peakAngleChangeRef.current = 0;
    peakDepthDuringRepRef.current = 0;
    audioCooldownRef.current = {};
    repDurationsRef.current = [];
    baselineRef.current = null;
    constraintViolationCountRef.current = 0;
    personalMaxDepthRef.current = 0;
  }

  useEffect(() => {
    if (!pendingStartRef.current || !videoRef.current) return;
    pendingStartRef.current = false;
    startCamera(facingMode);
  }, [showPlacementGuide, startCamera, videoRef, facingMode]);

  // Rep counting with multi-signal fatigue
  useEffect(() => {
    if (!exerciseAnalysis) return;
    const { depthPct, phase: newPhase, checkpoints, displayAngles, raw, bodyVisibility } = exerciseAnalysis;

    // MIRROR mode: skeleton + see-yourself only. No rep state machine, no
    // counting, no form scoring — used for holds & follow-along movements the
    // camera can't reliably rep-count.
    if (mode === 'mirror') {
      setPhase(newPhase);
      return;
    }

    if (onMetricsUpdate) {
      const allReps = repDurationsRef.current;
      const lastRep = allReps.length > 0 ? allReps[allReps.length - 1] : null;
      onMetricsUpdate({
        depthPercentage: depthPct,
        symmetry: exerciseAnalysis.symmetry,
        phase: newPhase,
        checkpoints, displayAngles, raw,
        timestamp: Date.now(),
        fatigue: {
          lastRepDuration: lastRep?.duration || null,
          baselineDuration: baselineRef.current?.duration || null,
          totalRepsTracked: allReps.length,
        }
      });
    }

    // ===== 3D ANGLE-DRIVEN STATE MACHINE =====
    const pm = exerciseAnalysis.primaryMotion;
    if (pm && pm.angle !== null) {
      const now = Date.now();
      const state = repStateRef.current;
      const cooldownMs = exerciseDefinition?.rep_thresholds?.cooldownMs || 650;
      // Direction-normalized velocity: <0 = moving TOWARD bottom, >0 = moving
      // back TOWARD start — works for flexion-first (dir=1) AND extension-first
      // (dir=-1) movements. Keeps squat/pushup behaviour identical (dir=1).
      const vel = pm.velocity * (pm.dir || 1);

      if (state === 'idle') {
        // Wait in start range AND setup check must pass (body in correct position)
        if (pm.inStartRange && exerciseAnalysis.setupCheckPassed) {
          startAngleRef.current = pm.angle;
        } else if (startAngleRef.current !== null && vel < -0.3 && exerciseAnalysis.setupCheckPassed) {
          // Angle is moving away from start (toward bottom) → movement started
          repStateRef.current = 'descending';
          repStartTimeRef.current = now;
          peakAngleChangeRef.current = 0;
          peakDepthDuringRepRef.current = pm.depthPct;
          constraintViolationCountRef.current = 0;
          // Reset per-rep joint-angle accumulator (min/max/sum/count for each declared angle).
          // From here, every frame until rep-complete contributes one sample.
          repAngleAccumRef.current = {};
        }
      } else if (state === 'descending') {
        // Track amplitude
        if (startAngleRef.current !== null) {
          const change = Math.abs(startAngleRef.current - pm.angle);
          if (change > peakAngleChangeRef.current) peakAngleChangeRef.current = change;
        }
        // Reached bottom range
        if (pm.inBottomRange) {
          repStateRef.current = 'bottom';
          deepStartTimeRef.current = now;
        }
        // Abort: returned to start without reaching bottom
        if (pm.inStartRange && vel > 0.3) {
          repStateRef.current = 'idle';
          startAngleRef.current = pm.angle;
        }
      } else if (state === 'bottom') {
        // Track amplitude
        if (startAngleRef.current !== null) {
          const change = Math.abs(startAngleRef.current - pm.angle);
          if (change > peakAngleChangeRef.current) peakAngleChangeRef.current = change;
        }
        // Ascending: leaving bottom range, moving back toward start
        if (!pm.inBottomRange && vel > 0.3) {
          repStateRef.current = 'ascending';
        }
      } else if (state === 'ascending') {
        // Complete: returned to start range AND top_position conditions met
        if (pm.inStartRange && exerciseAnalysis.topPositionMet) {
          const repDuration = now - repStartTimeRef.current;
          const cooldownOk = (now - repCooldownRef.current) > cooldownMs;
          const durationOk = repDuration > 500 && repDuration < 15000;
          const amplitudeOk = peakAngleChangeRef.current >= pm.minAmplitude;
          const sustainedBottom = (now - deepStartTimeRef.current) > 150;

          if (cooldownOk && durationOk && amplitudeOk && sustainedBottom) {
            const repDurationSec = repDuration / 1000;
            const repNum = (repDurationsRef.current.length || 0) + 1;
            const dangerCount = checkpoints?.filter(c => c.status === 'danger').length || 0;
            const warnCount = checkpoints?.filter(c => c.status === 'warning').length || 0;
            const constraintPenalty = Math.min(30, constraintViolationCountRef.current * 3);
            const formScore = Math.max(0, 100 - (dangerCount * 30) - (warnCount * 10) - constraintPenalty);

            // Personal calibration: update max depth from first 3 reps
            const rawPeakDepth = peakDepthDuringRepRef.current;
            if (repNum <= 3 && rawPeakDepth > personalMaxDepthRef.current) {
              personalMaxDepthRef.current = rawPeakDepth;
            }
            // Personal depth: how deep THIS rep was vs YOUR personal max
            const personalDepth = personalMaxDepthRef.current > 0
              ? Math.min(100, (rawPeakDepth / personalMaxDepthRef.current) * 100) : rawPeakDepth;

            repDurationsRef.current.push({
              repNum, duration: repDuration, peakDepth: personalDepth,
              rawDepth: rawPeakDepth,
              symmetry: exerciseAnalysis?.symmetry ?? null, formScore,
            });

            const allReps = repDurationsRef.current;
            if (allReps.length === 3 && !baselineRef.current) {
              // Use median of first 3 rep durations as tempo baseline (robust to outlier warmup rep)
              const sorted = [...allReps].map(r => r.duration).sort((a, b) => a - b);
              const medianDur = sorted[1];
              baselineRef.current = {
                duration: allReps.reduce((s, r) => s + r.duration, 0) / 3,
                medianDuration: medianDur,
                depth: allReps.reduce((s, r) => s + r.peakDepth, 0) / 3,
                symmetry: allReps.filter(r => r.symmetry !== null).length > 0
                  ? allReps.filter(r => r.symmetry !== null).reduce((s, r) => s + r.symmetry, 0) / allReps.filter(r => r.symmetry !== null).length : null,
                formScore: allReps.reduce((s, r) => s + r.formScore, 0) / 3,
                intent: tempoIntent(medianDur / 1000).key,
              };
              setPaceStatus({
                key: 'on-pace',
                label: 'On pace',
                tone: 'good',
                intentLabel: tempoIntent(medianDur / 1000).label,
              });
            }

            // Live pace check: after rep 4+, compare current rep duration vs baseline median
            if (baselineRef.current && allReps.length >= 4) {
              const base = baselineRef.current.medianDuration;
              const pctSlower = ((repDuration - base) / base) * 100;
              let next;
              if (pctSlower >= 40) next = { key: 'struggling', label: 'Struggling', tone: 'danger' };
              else if (pctSlower >= 15) next = { key: 'slowing', label: 'Slowing down', tone: 'warning' };
              else if (pctSlower <= -20) next = { key: 'faster', label: 'Moving faster', tone: 'good' };
              else next = { key: 'on-pace', label: 'On pace', tone: 'good' };
              next.intentLabel = tempoIntent(base / 1000).label;
              setPaceStatus(next);
            }

            let fatigueSignal = 'fresh';
            let fatigueScore = 0;
            if (baselineRef.current && allReps.length >= 4) {
              const recent = allReps.slice(-3);
              const bl = baselineRef.current;
              const rd = recent.reduce((s, r) => s + r.duration, 0) / recent.length;
              const dF = Math.max(0, Math.min(100, (bl.duration > 0 ? ((rd - bl.duration) / bl.duration) * 100 : 0) * 2.5));
              const rD = recent.reduce((s, r) => s + r.peakDepth, 0) / recent.length;
              const dpF = Math.max(0, Math.min(100, (bl.depth > 0 ? ((bl.depth - rD) / bl.depth) * 100 : 0) * 3));
              let sF = 0;
              if (bl.symmetry !== null) {
                const sr = recent.filter(r => r.symmetry !== null);
                if (sr.length > 0) sF = Math.max(0, Math.min(100, ((bl.symmetry - sr.reduce((s, r) => s + r.symmetry, 0) / sr.length) / bl.symmetry) * 100 * 4));
              }
              const rF = recent.reduce((s, r) => s + r.formScore, 0) / recent.length;
              const fF = Math.max(0, Math.min(100, (bl.formScore > 0 ? ((bl.formScore - rF) / bl.formScore) * 100 : 0) * 2.5));
              fatigueScore = Math.round(dF * 0.35 + dpF * 0.25 + sF * 0.20 + fF * 0.20);
              if (fatigueScore > 60) fatigueSignal = 'high';
              else if (fatigueScore > 35) fatigueSignal = 'moderate';
              else if (fatigueScore > 15) fatigueSignal = 'mild';
            }

            repCooldownRef.current = now;
            // Compute per-angle {min, max, avg} from the accumulator (full rep range).
            // Backend will pick the correct one (min/max/avg) per angle using each
            // exercise definition's own direction info.
            const repJointAngles = {};
            for (const [name, a] of Object.entries(repAngleAccumRef.current)) {
              if (name.startsWith('_')) continue;   // skip internal buffers
              if (a && a.count > 0) {
                repJointAngles[name] = {
                  min: Math.round(a.min),
                  max: Math.round(a.max),
                  avg: Math.round(a.sum / a.count),
                };
              }
            }
            setRepCount(prev => {
              const n = prev + 1;
              if (onRepComplete) onRepComplete(n, {
                ...exerciseAnalysis, fatigueSignal, fatigueScore, repDuration,
                repDurationSec: Math.round(repDurationSec * 10) / 10, formScore,
                jointAngles: repJointAngles,  // override snapshot with full-rep min/max/avg
              });
              if (audioFeedback && speechSynthRef.current) {
                const u = new SpeechSynthesisUtterance(`${n}`);
                u.rate = 1.2;
                speechSynthRef.current.speak(u);
              }
              return n;
            });
          }
          repStateRef.current = 'idle';
          startAngleRef.current = pm.angle;
        }
      }

      // Track constraint violations during active rep
      if (state !== 'idle' && !exerciseAnalysis.constraintsOk) {
        constraintViolationCountRef.current++;
      }

      // Per-frame joint-angle sampler — runs the whole time the rep is active (not idle).
      // Captures min/max/sum/count for every named angle so we can compute the right value
      // (min for flexed-at-bottom, max for extended-at-top, avg for "throughout" angles)
      // when the rep completes.
      //
      // Robustness — multiple gates already filtered out bad frames upstream
      // (3D depth-stability gate, 5-frame median per checkpoint, skeleton sanity
      // checks). This is the LAST line of defence: per-rep outlier rejection.
      // Each frame's smoothed angle is compared against the rep's running median;
      // anything more than 40° away gets dropped. This catches the rare case
      // where MediaPipe drifts confidently wrong for an extended stretch.
      if (state !== 'idle' && exerciseAnalysis.jointAngles) {
        const acc = repAngleAccumRef.current;
        if (!acc._buf) acc._buf = {};
        if (!acc._samples) acc._samples = {};
        const REP_MEDIAN_WINDOW = 8;
        const REP_OUTLIER_DEG = 40;

        for (const [name, value] of Object.entries(exerciseAnalysis.jointAngles)) {
          if (value === null || value === undefined || isNaN(value)) continue;
          const v = Number(value);

          // Outlier reject vs the rep's running median (only after we have a few samples)
          const samples = acc._samples[name] || (acc._samples[name] = []);
          if (samples.length >= 3) {
            const sorted = [...samples].sort((a, b) => a - b);
            const repMedian = sorted[Math.floor(sorted.length / 2)];
            if (Math.abs(v - repMedian) > REP_OUTLIER_DEG) {
              continue;   // skip this frame — it's drifted way off the rep's normal
            }
          }
          samples.push(v);
          if (samples.length > REP_MEDIAN_WINDOW) samples.shift();

          if (!acc[name]) {
            acc[name] = { min: v, max: v, sum: v, count: 1 };
          } else {
            acc[name].min = Math.min(acc[name].min, v);
            acc[name].max = Math.max(acc[name].max, v);
            acc[name].sum += v;
            acc[name].count += 1;
          }
        }
      }
      // Track peak depth during active rep
      if (state !== 'idle' && pm.depthPct > peakDepthDuringRepRef.current) {
        peakDepthDuringRepRef.current = pm.depthPct;
      }
    }

    setPhase(newPhase);

    // Smart audio cues
    if (audioFeedback && speechSynthRef.current && showLiveFeedback) {
      const now = Date.now();
      const dangers = checkpoints?.filter(c => c.status === 'danger') || [];
      for (const issue of dangers) {
        const key = issue.label;
        const lastSpoken = audioCooldownRef.current[key] || 0;
        if (now - lastSpoken > 12000) {
          audioCooldownRef.current[key] = now;
          const u = new SpeechSynthesisUtterance(issue.tip);
          u.rate = 1.05; u.volume = 0.85;
          speechSynthRef.current.speak(u);
          break;
        }
      }
    }
  }, [exerciseAnalysis, exerciseName, exerciseDefinition, audioFeedback, showLiveFeedback, onMetricsUpdate, onRepComplete, mode]);

  useEffect(() => { if ('speechSynthesis' in window) speechSynthRef.current = window.speechSynthesis; }, []);

  useEffect(() => {
    if (isRecording && !is3DRecording && cameraActive) startRecording();
    else if (!isRecording && is3DRecording) { const f = stopRecording(); if (onRecordingComplete) onRecordingComplete(f); }
  }, [isRecording, is3DRecording, cameraActive, startRecording, stopRecording, onRecordingComplete]);

  const handleStartCamera = useCallback(() => {
    setShowPlacementGuide(false);
    if (videoRef.current) startCamera(facingMode);
    else pendingStartRef.current = true;
  }, [startCamera, videoRef, facingMode]);

  const hasLiveFatigue = Object.keys(liveFatigue).length > 0;

  if (error) {
    return (
      <div className="hpme-card p-8" data-testid="pose-camera-error">
        <div className="text-center mb-6">
          <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-yellow-500 font-semibold text-lg mb-2">Camera Not Available</p>
          <p className="text-zinc-400">{error}</p>
        </div>
        <div className="bg-zinc-800/50 p-4 rounded-sm mb-4">
          <p className="font-semibold text-sm mb-2">To enable camera access:</p>
          <ol className="text-sm text-zinc-400 space-y-2 list-decimal list-inside">
            <li>Click the lock icon in your browser address bar</li>
            <li>Set Camera to &quot;Allow&quot;</li>
            <li>Reload the page</li>
          </ol>
        </div>
        <Button className="hpme-btn-primary" onClick={() => window.location.reload()}>Reload Page</Button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={immersive ? 'relative flex flex-col flex-1 min-h-0' : 'relative'} data-testid="pose-camera-advanced">
      {/* Placement Guide */}
      {showPlacementGuide && (
        <div className="hpme-card p-6 mb-4" data-testid="camera-placement-guide">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Camera className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-secondary font-bold text-lg mb-1">
                {placement.view} VIEW — {exerciseName.toUpperCase()}
              </h3>
              <p className="text-zinc-400 text-sm mb-3">{placement.desc}</p>
            </div>
          </div>
          <Button className="hpme-btn-primary w-full" onClick={handleStartCamera} data-testid="start-camera-btn">
            <Camera className="w-5 h-5 mr-2" /> Start Camera
          </Button>
        </div>
      )}

      {/* Main camera view */}
      <div className={immersive ? 'flex-col flex-1 min-h-0' : ''} style={{ display: showPlacementGuide ? 'none' : (immersive ? 'flex' : 'block') }}>
        {isLoading && (
          <div className="hpme-card p-8 text-center mb-4" data-testid="pose-camera-loading">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading pose detection model...</p>
          </div>
        )}

        <div className={`flex flex-col lg:flex-row gap-4 ${immersive ? 'flex-1 min-h-0' : ''}`}>
          {/* Video column */}
          <div className={`flex-1 min-w-0 ${immersive ? 'flex flex-col min-h-0' : ''}`}>
            {/* HARD view requirement: some movements (e.g. bent-over row) travel
                toward/away from the camera in every view except one, so they
                literally can't be tracked otherwise. Show a blocking-style
                banner telling the user to turn. */}
            {cameraActive && exerciseDefinition?.requires_view && exerciseAnalysis?.cameraView
              && exerciseAnalysis.cameraView !== exerciseDefinition.requires_view && (
              <div className="mb-3 bg-red-500/15 border border-red-500/50 rounded-sm p-3 flex items-start gap-3" data-testid="requires-view-banner">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-secondary font-bold text-sm text-red-300 tracking-wide">
                    Turn 90° — film from the {exerciseDefinition.requires_view}
                  </p>
                  <p className="text-xs text-red-200/80 mt-0.5">
                    {exerciseName || 'This move'} can only be tracked from the {exerciseDefinition.requires_view}. From the {exerciseAnalysis.cameraView} the motion moves toward the camera, so reps won't count.
                  </p>
                </div>
              </div>
            )}

            {/* View-aware placement guide */}
            {cameraActive && exerciseAnalysis?.cameraView && (
              <div className={`mb-3 bg-zinc-900/60 border border-zinc-800 rounded-sm p-3 items-start gap-3 ${immersive ? 'hidden lg:flex' : 'flex'}`} data-testid="view-aware-guide">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${
                  exerciseAnalysis.cameraView === 'front' ? 'bg-primary/20 text-primary'
                  : exerciseAnalysis.cameraView === 'side' ? 'bg-blue-500/20 text-blue-400'
                  : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  <Camera className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-secondary font-bold text-sm tracking-wider" data-testid="detected-view-label">
                        {exerciseAnalysis.cameraView === 'front' ? 'FRONT VIEW'
                          : exerciseAnalysis.cameraView === 'side' ? 'SIDE VIEW'
                          : exerciseAnalysis.cameraView === 'angled' ? 'ANGLED VIEW'
                          : 'DETECTING…'}
                      </span>
                      {viewMode !== 'auto' && (
                        <span className="text-[9px] font-bold tracking-wider text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded-sm" data-testid="view-override-badge">
                          MANUAL
                        </span>
                      )}
                    </div>
                    {/* Auto / Front / Side pill toggle */}
                    <div className="flex bg-zinc-950 rounded-sm p-0.5 border border-zinc-800" data-testid="view-mode-toggle">
                      {['auto', 'front', 'side'].map(v => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => setViewMode(v)}
                          data-testid={`view-mode-${v}-btn`}
                          className={`text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded-sm transition-colors ${
                            viewMode === v ? 'bg-primary text-black' : 'text-zinc-400 hover:text-white'
                          }`}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    const metrics = getTrackableMetrics(exerciseDefinition, exerciseAnalysis.cameraView);
                    if (metrics.length === 0) {
                      return <p className="text-xs text-zinc-500">Stand in frame — metrics will appear shortly.</p>;
                    }
                    return (
                      <div className="flex flex-wrap gap-1.5" data-testid="trackable-metrics-list">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5">Tracking:</span>
                        {metrics.map((m, i) => (
                          <span
                            key={i}
                            data-testid={`trackable-metric-${m.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm border ${
                              m.primary
                                ? 'text-primary border-primary/40 bg-primary/10'
                                : 'text-zinc-300 border-zinc-700 bg-zinc-900'
                            }`}
                          >
                            {m.label}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                  {exerciseAnalysis.cameraView === 'angled' && viewMode === 'auto' && (
                    <p className="text-[10px] text-yellow-400 mt-1" data-testid="view-angled-hint">
                      Tip: Turn slightly left/right for cleaner metrics, or tap Front/Side to lock the view.
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className={`relative bg-black rounded-sm overflow-hidden ${immersive ? 'flex-1 min-h-0' : ''}`} data-testid="camera-view-container">
              {/* Single view: video + skeleton overlay on same frame.
                  Portrait-tall on phones (fills the screen like a real camera);
                  landscape 16:9 on tablet/desktop. */}
              <div className={immersive ? 'relative w-full h-full min-h-0 bg-black' : 'relative aspect-[9/16] sm:aspect-video bg-black'} data-testid="unified-camera-view">
                {/* Video feed */}
                <video ref={videoRef} className={`absolute inset-0 w-full h-full ${immersive ? 'object-cover sm:object-contain' : 'object-contain'} ${!cameraActive ? 'invisible' : ''}`} autoPlay playsInline muted />
                
                {/* Skeleton canvas overlaid directly on video */}
                <canvas ref={canvasRef} className={`absolute inset-0 w-full h-full ${immersive ? 'object-cover sm:object-contain' : 'object-contain'} ${!cameraActive ? 'invisible' : ''}`} style={{ pointerEvents: 'none' }} />

                {/* Start prompt when camera off */}
                {!cameraActive && !isLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-zinc-900/90 z-10">
                    <Camera className="w-16 h-16 text-zinc-500 mb-4" />
                    <Button className="hpme-btn-primary" onClick={startCamera} data-testid="start-camera-btn-inline">
                      <Camera className="w-5 h-5 mr-2" /> Start Camera
                    </Button>
                  </div>
                )}
                  
                {/* Recording indicator */}
                {cameraActive && is3DRecording && (
                  <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-500/90 px-3 py-1 rounded-sm z-10">
                    <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    <span className="text-white text-xs font-bold">REC</span>
                  </div>
                )}

                {/* iter68b wellness chrome — top-left cluster:
                    Only the things the user asked for: REP COUNT (primary, neon
                    yellow), SET # (secondary, smaller). Phase + pace badge were
                    technical noise (the body knows what phase it's in) — removed.
                    Everything else is moved to the post-session summary. */}
                {cameraActive && mode === 'mirror' && (
                  <div className="absolute top-3 left-3 flex items-center gap-2" style={{ top: is3DRecording ? '44px' : '12px' }}>
                    <div className="bg-zinc-950/85 backdrop-blur border border-cyan-500/40 px-3 py-1.5 rounded-sm flex items-center gap-1.5" data-testid="mirror-badge">
                      <Camera className="w-3.5 h-3.5 text-cyan-300" />
                      <span className="text-[10px] text-cyan-200 uppercase tracking-widest font-bold">Mirror · not counting</span>
                    </div>
                  </div>
                )}
                {cameraActive && mode !== 'mirror' && (
                  <div className="absolute top-3 left-3 flex items-center gap-2" style={{ top: is3DRecording ? '44px' : '12px' }}>
                    <div className="bg-zinc-950/85 backdrop-blur border border-zinc-700/50 px-4 py-1.5 rounded-sm" data-testid="rep-counter-overlay">
                      <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Rep</span>
                      <p className="hpme-number text-3xl text-primary leading-tight">{repCount}</p>
                    </div>
                    {setNumber != null && (
                      <div className="bg-zinc-950/85 backdrop-blur border border-zinc-700/50 px-3 py-1.5 rounded-sm" data-testid="set-counter-overlay">
                        <span className="text-[9px] text-zinc-500 uppercase tracking-widest">Set</span>
                        <p className="font-secondary font-bold text-xl text-zinc-300 leading-tight">{setNumber}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* iter68b wellness chrome — ONE-LINE BOTTOM TIP.
                    Replaces the loud `form-labels-display` (full row of colored
                    joints) and the raw `angle-display` fallback. Surfaces a single
                    coaching line only when there's a REAL fault — i.e. at least
                    one form_label is colored red. Otherwise stays quiet. The full
                    per-joint breakdown still appears in the post-session summary
                    where the user can study it without it polluting the live view. */}
                {cameraActive && mode !== 'mirror' && exerciseAnalysis?.formLabels && (() => {
                  const fault = exerciseAnalysis.formLabels.find(fl => (fl.color || '').toLowerCase().includes('ef4444') || (fl.color || '').toLowerCase() === '#ef4444' || (fl.color || '').toLowerCase().includes('red'));
                  if (!fault) return null;
                  return (
                    <div className="absolute bottom-3 left-3 right-3 bg-zinc-950/85 backdrop-blur border border-zinc-700/50 px-3 py-2 rounded-sm flex items-center gap-2" data-testid="live-tip">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                      <span className="text-zinc-400 text-xs uppercase tracking-wider">{fault.bodyPart}:</span>
                      <span className="text-xs font-secondary font-bold text-white">{fault.label}</span>
                    </div>
                  );
                })()}

                {/* PRE-WORKOUT FRAMING COACH — only before the first rep of the exercise.
                    Once the user starts moving (repCount > 0) the overlay disappears so it
                    never blocks the live feedback. Uses the exercise definition's own
                    required_landmarks (via missingParts) + setup_check to decide what to say.
                    iter68: the redundant `body-visibility-warning` (which fired in parallel
                    and showed the same info) was removed — this single coach is now the only
                    framing surface, with softer wellness-aligned copy. */}
                {cameraActive && mode !== 'mirror' && repCount === 0 && exerciseAnalysis && (() => {
                  const missing = exerciseAnalysis.missingParts || [];
                  const setupOk = exerciseAnalysis.setupCheckPassed !== false;
                  const allGood = missing.length === 0 && setupOk;
                  let title, hint, tone;
                  if (allGood) {
                    title = "I can see you — start whenever you're ready.";
                    hint = 'Take a breath. First rep when you feel set.';
                    tone = 'good';
                  } else if (missing.length > 0) {
                    title = "I can't see all of you yet";
                    hint = `Step back a bit so I catch your ${missing.join(' and ')}.`;
                    tone = 'warn';
                  } else {
                    title = 'Settle into your starting position';
                    hint = 'Hold the stance for a second so I can lock on.';
                    tone = 'warn';
                  }
                  const iconColor = tone === 'good' ? '#a3e635' : '#CCFF00';
                  return (
                    <div className="absolute top-[120px] left-3 right-3 bg-zinc-950/85 backdrop-blur border border-zinc-700/60 px-3 py-2 rounded-sm flex items-start gap-2 z-10" data-testid="framing-coach">
                      {tone === 'good'
                        ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: iconColor }} />
                        : <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: iconColor }} />}
                      <div>
                        <p className="font-secondary font-bold text-xs text-white">{title}</p>
                        <p className="text-[10px] text-zinc-400 leading-snug mt-0.5">{hint}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Depth bar */}
                {cameraActive && mode !== 'mirror' && exerciseAnalysis?.depthPct !== undefined && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 bg-zinc-900/60 rounded-sm overflow-hidden" data-testid="depth-bar">
                    <div className="h-28 relative">
                      <div className="absolute bottom-0 left-0 right-0 transition-all duration-100" 
                        style={{ height: `${exerciseAnalysis.depthPct}%`, backgroundColor: exerciseAnalysis.depthPct > 60 ? '#00FF66' : exerciseAnalysis.depthPct > 30 ? '#FFB800' : '#CCFF00' }} />
                    </div>
                    <div className="text-center py-0.5 text-[9px] font-bold text-primary">{Math.round(exerciseAnalysis.depthPct)}%</div>
                  </div>
                )}

                {/* LIVE label + Settings */}
                {cameraActive && (
                  <>
                    <div className="absolute top-3 right-3 bg-zinc-900/70 px-2 py-0.5 rounded-sm">
                      <span className="text-[9px] text-zinc-400 font-mono uppercase tracking-wider">Live</span>
                    </div>
                    <button onClick={() => setShowSettings(s => !s)} className="absolute bottom-3 right-10 p-2 bg-zinc-800/80 rounded-sm hover:bg-zinc-700 transition-colors z-10" data-testid="ar-settings-btn">
                      <Settings2 className="w-4 h-4 text-zinc-400" />
                    </button>
                  </>
                )}

                {showSettings && (
                  <div className="absolute bottom-12 right-10 bg-zinc-900/95 backdrop-blur p-3 rounded-sm w-52 space-y-2 z-20" data-testid="ar-settings-panel">
                    <p className="font-secondary font-bold text-xs text-primary tracking-wider">SETTINGS</p>
                    <Toggle checked={showIdealPanel} onChange={setShowIdealPanel} label="Ideal Form Panel" />
                    <Toggle checked={audioFeedback} onChange={setAudioFeedback} label="Audio Feedback" />
                  </div>
                )}

                {/* Mini Body Map — bottom-left corner */}
                {cameraActive && hasLiveFatigue && (
                  <div className="absolute bottom-14 left-3 w-16 bg-zinc-900/85 backdrop-blur-sm rounded-sm border border-zinc-700/50 p-1" data-testid="mini-body-map-overlay">
                    <BodyMapSVG muscleData={liveFatigue} highlightMode="recovery" size="sm" showLabels={false} interactive={false} />
                    <p className="text-[7px] text-zinc-500 text-center font-mono">FATIGUE</p>
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className={`flex items-center justify-between ${immersive ? 'mt-2 flex-shrink-0' : 'mt-3'}`}>
              <div className="flex gap-2">
                {cameraActive ? (
                  <Button variant="ghost" className="text-zinc-400 hover:text-red-400 text-sm h-8" onClick={stopCamera} data-testid="stop-camera-btn">
                    <CameraOff className="w-4 h-4 mr-1" /> Stop
                  </Button>
                ) : (
                  <Button className="hpme-btn-primary h-8 text-sm" onClick={() => startCamera(facingMode)} data-testid="restart-camera-btn">
                    <Camera className="w-4 h-4 mr-1" /> Start
                  </Button>
                )}
                <Button variant="ghost" className="text-zinc-400 hover:text-white text-sm h-8" onClick={flipCamera} data-testid="flip-camera-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Flip
                </Button>
                <Button variant="ghost" className="text-zinc-400 hover:text-white text-sm h-8" onClick={() => { setRepCount(0); setPhase('standing'); repStateRef.current = 'idle'; startAngleRef.current = null; peakAngleChangeRef.current = 0; repStartTimeRef.current = 0; }} data-testid="reset-reps-btn">
                  <RotateCcw className="w-4 h-4 mr-1" /> Reset ({repCount})
                </Button>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className={audioFeedback ? 'text-primary' : 'text-zinc-500'} onClick={() => setAudioFeedback(v => !v)} data-testid="toggle-audio-btn">
                  {audioFeedback ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="sm" className="text-zinc-400" onClick={() => { if (!document.fullscreenElement) containerRef.current?.requestFullscreen(); else document.exitFullscreen(); }} data-testid="toggle-fullscreen-btn">
                  <Maximize2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Ideal Angles Side Panel */}
          {mode !== 'mirror' && showIdealPanel && cameraActive && exerciseAnalysis?.idealAngles && Object.keys(exerciseAnalysis.idealAngles).length > 0 && (
            <div className="w-56 flex-shrink-0 hidden lg:block" data-testid="ideal-angles-sidebar">
              <IdealAnglesPanel idealAngles={exerciseAnalysis.idealAngles} />
            </div>
          )}
        </div>

        {/* Checkpoints grid */}
        {cameraActive && mode !== 'mirror' && exerciseAnalysis?.checkpoints && exerciseAnalysis.checkpoints.length > 0 && (
          <div className={immersive ? 'hidden lg:block mt-4' : 'mt-4'} data-testid="checkpoints-section">
            <div className="flex items-center justify-between mb-2">
              <p className="font-secondary font-bold text-sm text-zinc-400 tracking-wider">FORM CHECKPOINTS</p>
              <span className="text-xs text-zinc-500 capitalize">{exerciseAnalysis.phase} phase</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {exerciseAnalysis.checkpoints.map((cp, i) => <Checkpoint key={i} cp={cp} />)}
            </div>
          </div>
        )}

        {/* Mobile ideal angles */}
        {mode !== 'mirror' && showIdealPanel && cameraActive && exerciseAnalysis?.idealAngles && Object.keys(exerciseAnalysis.idealAngles).length > 0 && (
          <div className={immersive ? 'hidden' : 'mt-4 lg:hidden'} data-testid="ideal-angles-mobile">
            <IdealAnglesPanel idealAngles={exerciseAnalysis.idealAngles} />
          </div>
        )}

      </div>
    </div>
  );
};

export default PoseCameraAdvanced;
